import { useLayoutEffect, useMemo, useRef, useState } from "react";

const PANEL = "programs.programInsights.panel";

/** Fallback when layout width is not ready yet (~narrow lift card title block). */
const DEFAULT_INCLUDES_WIDTH_PX = 220;

/** ~px per character at 0.625rem italic in the Includes line (conservative). */
const PX_PER_CHAR = 5.5;

/** Lines reserved for Includes (matches CSS two-line cap). */
const INCLUDES_LINE_COUNT = 2;

/**
 * @param {import("i18next").TFunction} t
 * @param {string[]} allNames
 * @param {number} visibleCount
 * @returns {string}
 */
export function buildIncludesDisplayText(t, allNames, visibleCount) {
  const preview = allNames.slice(0, visibleCount);
  const overflow = allNames.length - visibleCount;
  let text = t(`${PANEL}.includesLine`, { names: preview.join(", ") });
  if (overflow > 0) {
    text += ` ${t("programs.programInsights.panel.includesMoreCount", {
      count: overflow,
    })}`;
  }
  return text;
}

/**
 * Max characters that fit in the Includes box for a given width (two lines).
 * @param {number} widthPx
 * @returns {number}
 */
export function maxIncludesTextLengthForWidth(widthPx) {
  const safeWidth = widthPx > 0 ? widthPx : DEFAULT_INCLUDES_WIDTH_PX;
  const charsPerLine = Math.max(14, Math.floor(safeWidth / PX_PER_CHAR));
  return charsPerLine * INCLUDES_LINE_COUNT;
}

/**
 * Picks how many movement names to show before “+N more” using width-based char budget.
 *
 * @param {import("i18next").TFunction} t
 * @param {string[]} allNames
 * @param {number} widthPx
 * @returns {{ displayText: string, includesTitle: string|undefined, clamp: boolean }}
 */
export function fitIncludesNamesForWidth(t, allNames, widthPx) {
  const fullText = t(`${PANEL}.includesLine`, {
    names: allNames.join(", "),
  });
  const maxLen = maxIncludesTextLengthForWidth(widthPx);

  if (fullText.length <= maxLen) {
    return {
      displayText: fullText,
      includesTitle: undefined,
      clamp: true,
    };
  }

  let visibleCount = 0;
  for (let i = 1; i <= allNames.length; i += 1) {
    const candidate = buildIncludesDisplayText(t, allNames, i);
    if (candidate.length <= maxLen) {
      visibleCount = i;
    } else {
      break;
    }
  }

  if (visibleCount < 1) {
    visibleCount = 1;
  }

  let displayText = buildIncludesDisplayText(t, allNames, visibleCount);
  while (displayText.length > maxLen && visibleCount > 1) {
    visibleCount -= 1;
    displayText = buildIncludesDisplayText(t, allNames, visibleCount);
  }

  const overflow = allNames.length - visibleCount;
  return {
    displayText,
    includesTitle: overflow > 0 ? fullText : undefined,
    clamp: true,
  };
}

/**
 * Fits movement names into the two-line Includes box; “+N more” only when names remain hidden.
 *
 * @param {string[]|undefined} includesNames
 * @param {boolean} includesNoteKey
 * @param {string} liftId
 * @param {import("i18next").TFunction} t
 */
export function useFittedIncludesLine(
  includesNames,
  includesNoteKey,
  liftId,
  t
) {
  const ref = useRef(null);
  const [widthPx, setWidthPx] = useState(0);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) {
      return undefined;
    }

    const updateWidth = () => {
      setWidthPx(el.clientWidth);
    };

    updateWidth();

    if (typeof ResizeObserver === "undefined") {
      return undefined;
    }

    const observer = new ResizeObserver(updateWidth);
    observer.observe(el);
    return () => observer.disconnect();
  }, [includesNames, includesNoteKey, liftId]);

  const fitted = useMemo(() => {
    if (!includesNames?.length) {
      if (includesNoteKey) {
        const displayText = t(`${PANEL}.lifts.${liftId}.includes`);
        return {
          displayText,
          includesTitle: undefined,
          clamp: true,
        };
      }
      return null;
    }

    return fitIncludesNamesForWidth(t, includesNames, widthPx);
  }, [includesNames, includesNoteKey, liftId, t, widthPx]);

  return { ref, fitted };
}
